/**
 * Trigger a browser-native download (saves to the user's Downloads folder).
 *
 * Uses an <a> click for direct downloads. As a fallback for sandboxed
 * iframes that strip programmatic <a download> clicks, we drop in an
 * invisible <iframe> pointing at the URL — the server's
 * `Content-Disposition: attachment` header makes the browser route the
 * response to its download manager without navigating the page.
 */

import { API } from "./api";

const getAuthToken = () => {
  try {
    return localStorage.getItem("kerboom_token") || "";
  } catch {
    return "";
  }
};

/**
 * Download a file served by the backend (must return
 * `Content-Disposition: attachment`).
 *
 * @param {string} apiPath e.g. "/quotes/abc/pdf"
 */
export const downloadFromUrl = (apiPath) => {
  const token = getAuthToken();
  const sep = apiPath.includes("?") ? "&" : "?";
  const url = `${API}${apiPath}${token ? `${sep}token=${encodeURIComponent(token)}` : ""}`;

  // Strategy 1: synchronous <a download> click (works outside iframes
  // and in iframes that grant `allow-downloads`)
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = ""; // Hint browser to download — server filename via Content-Disposition wins
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    /* ignored, fall through to iframe */
  }

  // Strategy 2: hidden iframe — works in nearly every sandbox configuration.
  // The browser fetches the URL, receives Content-Disposition: attachment,
  // and routes the response straight to the download manager. The iframe
  // never navigates the page itself.
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = url;
  document.body.appendChild(iframe);
  // Remove after a long delay so streaming downloads aren't truncated
  setTimeout(() => {
    try { iframe.remove(); } catch { /* ignored */ }
  }, 60 * 1000);
};

/**
 * Download a client-built Blob (e.g. a CSV template generated locally).
 */
export const downloadOrOpenBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
};
